import { useState } from "react";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { useHero } from "@/hooks/useHero";
import { useTimeline } from "@/hooks/useTimeline";
import { heroContent } from "@/data/heroContent";
import { timelineEvents } from "@/data/timeline";
import { useEffect } from "react";

const heroFallback = heroContent.find((h) => h.page === "evidencias")!;

interface DocumentoDB {
  id: number;
  titulo: string;
  categoria?: string;
  ano?: number;
  mes?: string;
  tipo?: string;
  tamano?: string;
  descripcion?: string;
  archivo_url?: string;
  created_at?: string;
}

interface FiltroDB {
  id: number;
  nombre: string;
  tipo: "categoria" | "filtro";
  activo: boolean;
  orden: number;
}

// Helper: convierte link de Google Drive a URL directo
function toDriveDirectUrl(url: string): string {
  if (!url) return url;
  if (url.includes("googleusercontent.com") || url.includes("drive.google.com/uc?")) {
    return url;
  }
  const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) {
    return `https://lh3.googleusercontent.com/d/${fileMatch[1]}`;
  }
  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (openMatch) {
    return `https://lh3.googleusercontent.com/d/${openMatch[1]}`;
  }
  return url;
}

const socials = [
  { icon: "ri-twitter-x-fill", label: "X", href: "https://x.com" },
  { icon: "ri-youtube-fill", label: "YouTube", href: "https://youtube.com" },
  { icon: "ri-instagram-fill", label: "Instagram", href: "https://instagram.com" },
  { icon: "ri-facebook-fill", label: "Facebook", href: "https://facebook.com" },
  { icon: "ri-linkedin-fill", label: "LinkedIn", href: "https://linkedin.com" },
];

export default function Evidencias() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDocId, setSelectedDocId] = useState(104);
  const { data: documentos, loading: docsLoading } = useSupabaseData<DocumentoDB>("documentos", []);
  const { data: evidenciasHero } = useHero("evidencias", heroFallback);
  const { data: filtros = [] } = useSupabaseData<FiltroDB>("evidencias_filtros", []);
  const { data: events } = useTimeline(timelineEvents);

  // Update page title from hero seo_title
  useEffect(() => {
    const seoTitle = evidenciasHero?.seo_title ?? heroFallback.seoTitle ?? "Evidencias | MásMed Tarapacá";
    if (seoTitle) {
      document.title = seoTitle;
    }
  }, [evidenciasHero]);

  const categorias = filtros
    .filter((f) => f.tipo === "categoria" && f.activo)
    .sort((a, b) => a.orden - b.orden);

  const filtrosActivos = filtros
    .filter((f) => f.tipo === "filtro" && f.activo)
    .sort((a, b) => a.orden - b.orden);

  const filteredDocs = documentos.filter((d) =>
    !searchQuery || d.titulo.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedDoc = documentos.find((d) => d.id === selectedDocId) || filteredDocs[0];

  const docsByYear = filteredDocs.reduce((acc, doc) => {
    const year = doc.ano ?? 0;
    if (!acc[year]) acc[year] = [];
    acc[year].push(doc);
    return acc;
  }, {} as Record<number, DocumentoDB[]>);

  const years = Object.keys(docsByYear).map(Number).sort((a, b) => b - a);

  return (
    <div>
      {/* Hero Evidencias */}
      <section className="relative w-full h-[580px] sm:h-[640px] md:h-[720px] lg:h-[760px] flex flex-col overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${evidenciasHero?.background_image ?? heroFallback.backgroundImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-700/60 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-cyan-600/25 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-l from-orange-500/30 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-black/10" />

        <div className="relative z-10 flex-1 flex flex-col items-center justify-between w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-10 md:pb-12">
          <div className="hidden md:flex absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 flex-col gap-3 lg:gap-4 z-20">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="w-10 h-10 lg:w-11 lg:h-11 rounded-full border-2 border-white/80 bg-white/10 hover:bg-white/25 hover:border-white hover:scale-110 flex items-center justify-center text-white text-base lg:text-lg transition-all duration-300 cursor-pointer backdrop-blur-sm"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={social.icon}></i>
                </div>
              </a>
            ))}
          </div>

          <div className="flex-1 flex flex-col items-center justify-center w-full">
            <div className="text-center max-w-3xl">
              <h1 className="font-utendo text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight drop-shadow-lg">
                Evidencias
              </h1>
              <p className="text-white/90 text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto mt-4 md:mt-5 drop-shadow"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
                Información clave para comprender la realidad de la salud en Tarapacá y apoyar la toma de decisiones.
              </p>
              <div className="flex md:hidden flex-row justify-center gap-3 mt-4">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-9 h-9 rounded-full border-2 border-white/80 bg-white/10 hover:bg-white/25 hover:border-white flex items-center justify-center text-white text-sm transition-all duration-300 cursor-pointer backdrop-blur-sm"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={social.icon}></i>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full max-w-4xl mx-auto text-center">
            <p className="text-white text-xs sm:text-sm md:text-base leading-relaxed drop-shadow-md max-w-3xl mx-auto"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
              Proyecto financiado por el Gobierno Regional de Tarapacá y su Consejo Regional a través del Fondo Regional para la Productividad y Desarrollo (FRPD).
            </p>
            <div className="mt-3 md:mt-4 flex flex-wrap items-center justify-center gap-3 md:gap-5 lg:gap-8">
              <span className="text-white text-xs md:text-sm uppercase tracking-wider drop-shadow-md"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>Financia:</span>
              <img src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/6576e2ace5ad881c20d8c13d63df08f0.png" alt="Gobierno Regional de Tarapacá" className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain drop-shadow-md" loading="lazy" />
              <img src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/cea5546c3b2b72c7ff9e242a7eae203e.png" alt="CORE Tarapacá" className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md" loading="lazy" />
              <span className="text-white text-xs md:text-sm uppercase tracking-wider drop-shadow-md"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>Ejecuta:</span>
              <img src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/200e423cba0b1ea675826f32c4682d50.png" alt="UNAP Universidad Arturo Prat" className="h-8 sm:h-9 md:h-10 lg:h-12 w-auto object-contain drop-shadow-md" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Mensaje introductorio */}
      <section className="pt-10 md:pt-12 pb-4 md:pb-6 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 flex items-start gap-4">
              <div className="w-1 self-stretch bg-primary rounded-full flex-shrink-0" />
              <div>
                <p className="text-foreground-700 text-sm md:text-base leading-relaxed"
                style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
                  A medida que avance el proyecto MásMed Tarapacá, esta sección irá incorporando los principales avances, resultados, estudios y hallazgos generados por el equipo, con el fin de compartir evidencia relevante y mantener informada a la comunidad sobre el desarrollo de la iniciativa.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buscador */}
      <section className="pt-4 md:pt-6 pb-2 md:pb-4 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-full shadow-lg px-5 md:px-6 h-[50px] md:h-[56px] flex items-center gap-3 border border-masmed-gray-100">
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 text-sm md:text-base text-masmed-gray-900 placeholder-masmed-gray-400 focus:outline-none bg-transparent"
              />
              <i className="ri-search-line text-masmed-gray-500 text-xl flex-shrink-0" />
            </div>
          </div>
        </div>
      </section>

      {/* Categorías simples */}
      <section className="pt-4 md:pt-6 pb-6 md:pb-8 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {categorias.map((cat) => (
              <div key={cat.nombre} className="bg-masmed-gray-50 border border-masmed-gray-200 rounded-lg flex items-stretch overflow-hidden">
                <div className="w-1.5 bg-primary flex-shrink-0" />
                <span className="flex-1 py-3.5 px-4 text-sm md:text-base text-masmed-gray-900 text-center"
                style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>{cat.nombre}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filtros */}
      <section className="pb-6 md:pb-8 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-wrap gap-2">
            <button className="px-3 py-2 rounded-full bg-white border border-masmed-gray-200 shadow-sm text-xs md:text-sm text-masmed-gray-600 flex items-center gap-1.5 cursor-pointer"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
              <i className="ri-equalizer-line" />
              Filtros
            </button>
            {filtrosActivos.map((f) => (
              <button key={f.id} className="px-3 py-2 rounded-full bg-white border border-masmed-gray-200 shadow-sm text-xs md:text-sm text-masmed-gray-600 cursor-pointer"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                {f.nombre}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Visor documental */}
      <section className="pb-12 md:pb-16 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="border border-masmed-gray-200 rounded-xl bg-masmed-gray-50 overflow-hidden flex flex-col lg:flex-row min-h-[500px] lg:min-h-[580px]">
            <div className="lg:w-[45%] xl:w-[40%] border-b lg:border-b-0 lg:border-r border-masmed-gray-200 flex flex-col">
              <div className="flex-1 overflow-y-auto p-4 md:p-5 max-h-[400px] lg:max-h-none">
                {years.length === 0 ? (
                  <p className="text-masmed-gray-400 text-sm text-center py-6">No se encontraron documentos</p>
                ) : (
                  years.map((year) => (
                    <div key={year} className="mb-5">
                      <h3 className="text-masmed-gray-900 text-sm md:text-base mb-2">{year}</h3>
                      <div className="space-y-1">
                        {docsByYear[year].map((doc) => (
                          <div
                            key={doc.id}
                            onClick={() => setSelectedDocId(doc.id)}
                            className={`flex items-center justify-between py-2 px-2 rounded-md cursor-pointer text-xs md:text-sm ${
                              selectedDocId === doc.id
                                ? "bg-white shadow-sm border border-masmed-gray-100"
                                : "hover:bg-white/50"
                            }`}
                          >
                            <span className={`truncate ${selectedDocId === doc.id ? "text-primary" : "text-masmed-gray-700"}`}>
                              {doc.titulo}.{doc.tipo}
                            </span>
                            {selectedDocId === doc.id && (
                              <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                <button className="text-[10px] md:text-xs px-2 py-0.5 rounded bg-primary text-white cursor-pointer"
                                style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>Ver</button>
                                <button className="text-[10px] md:text-xs px-2 py-0.5 rounded border border-masmed-gray-300 text-masmed-gray-600 cursor-pointer">Descargar</button>
                                <span className="text-[10px] text-masmed-gray-400">{doc.tamano}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="lg:flex-1 bg-white p-4 md:p-6">
              {selectedDoc && (
                <div className="h-full flex flex-col">
                  <h3 className="text-masmed-gray-900 text-sm md:text-base mb-2">
                    {selectedDoc.titulo}.{selectedDoc.tipo}
                  </h3>
                  <p className="text-masmed-gray-500 text-xs md:text-sm leading-relaxed mb-4">
                    {selectedDoc.descripcion || "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat."}
                  </p>
                  <div className="flex-1 bg-masmed-gray-50 rounded-lg border border-masmed-gray-200 flex items-center justify-center mb-4 min-h-[200px]">
                    {selectedDoc.archivo_url ? (
                      <iframe
                        src={toDriveDirectUrl(selectedDoc.archivo_url)}
                        className="w-full h-full min-h-[300px] lg:min-h-[400px] rounded-lg"
                        title={selectedDoc.titulo}
                      />
                    ) : (
                      <div className="text-center p-6">
                        <i className="ri-file-text-line text-masmed-gray-300 text-5xl md:text-6xl" />
                        <p className="text-masmed-gray-400 text-xs mt-2">Vista previa del documento</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <button className="w-8 h-8 rounded border border-masmed-gray-200 flex items-center justify-center text-masmed-gray-500 cursor-pointer hover:bg-masmed-gray-50 transition-colors">
                        <i className="ri-arrow-left-s-line" />
                      </button>
                      <span className="text-xs md:text-sm text-masmed-gray-600">Página 1 de 19</span>
                      <button className="w-8 h-8 rounded border border-masmed-gray-200 flex items-center justify-center text-masmed-gray-500 cursor-pointer hover:bg-masmed-gray-50 transition-colors">
                        <i className="ri-arrow-right-s-line" />
                      </button>
                    </div>
                    <span className="text-xs md:text-sm text-masmed-gray-500">{selectedDoc.tamano}</span>
                  </div>
                  <div className="flex gap-2">
                    {selectedDoc.archivo_url ? (
                      <a
                        href={toDriveDirectUrl(selectedDoc.archivo_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 rounded bg-primary text-white text-xs md:text-sm cursor-pointer hover:bg-primary-dark transition-colors"
                        style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                      >
                        Ver archivo
                      </a>
                    ) : (
                      <button className="px-4 py-2 rounded bg-primary text-white text-xs md:text-sm cursor-pointer hover:bg-primary-dark transition-colors"
                      style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
                        Ver archivo
                      </button>
                    )}
                    {selectedDoc.archivo_url ? (
                      <a
                        href={toDriveDirectUrl(selectedDoc.archivo_url)}
                        download
                        className="px-4 py-2 rounded border border-masmed-gray-300 text-masmed-gray-700 text-xs md:text-sm cursor-pointer hover:bg-masmed-gray-50 transition-colors"
                        style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}
                      >
                        Descargar
                      </a>
                    ) : (
                      <button className="px-4 py-2 rounded border border-masmed-gray-300 text-masmed-gray-700 text-xs md:text-sm cursor-pointer hover:bg-masmed-gray-50 transition-colors"
                      style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                        Descargar
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Cronología */}
      <section className="relative w-full overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/db2ee516f857ca9d9815d94022c7bc02.jpeg)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-700/25 via-transparent to-orange-700/25" />
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative z-10 py-14 md:py-18 lg:py-24">
          <div className="max-w-[1180px] mx-auto px-4 md:px-8 lg:px-12">
            {/* Título centrado */}
            <div className="text-center mb-3 md:mb-4">
              <h2 className="text-2xl md:text-[28px] text-white tracking-tight drop-shadow">
                Cronología
              </h2>
            </div>

            {/* Línea ondulada decorativa naranja */}
            <div className="flex justify-center mb-10 md:mb-14 lg:mb-16">
              <svg viewBox="0 0 320 12" className="w-48 md:w-64 h-4" preserveAspectRatio="none">
                <path
                  d="M0,6 Q16,0 32,6 T64,6 T96,6 T128,6 T160,6 T192,6 T224,6 T256,6 T288,6 T320,6"
                  stroke="#E45B2D"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            {/* Timeline horizontal */}
            <div className="relative">
              {/* Línea de conexión horizontal */}
              <div className="absolute left-0 right-0 top-[90px] md:top-[102px] lg:top-[108px] h-[2px] bg-white/40" />

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-x-2 md:gap-x-4 lg:gap-x-5">
                {events.map((event) => {
                  const isFilled = event.orden <= 2;
                  const numStr = String(event.orden).padStart(2, "0");
                  // Usar estado_tipo y estado_color desde la BD
                  const hasEstado = !!event.estado_tipo;
                  const estadoColor = event.estado_color || "#1A7A7A";

                  return (
                    <div key={event.id} className="flex flex-col items-center text-center relative">
                      {/* Título arriba */}
                      <div className="mb-3 md:mb-4 h-[60px] md:h-[72px] flex items-end justify-center w-full px-1">
                        <p className="text-white text-sm md:text-base leading-snug font-medium text-center drop-shadow">
                          {event.titulo}
                        </p>
                      </div>

                      {/* Círculo con indicador de estado */}
                      <div className="relative flex items-center justify-center">
                        <div
                          className={`
                            w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full
                            flex items-center justify-center
                            text-base md:text-lg lg:text-xl
                            transition-colors duration-200
                            ${isFilled
                              ? "bg-primary text-white"
                              : "bg-white/10 text-white border-2 border-white/60"
                            }
                          `}
                          style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                        >
                          {numStr}
                        </div>

                        {/* Indicador de estado sobrepuesto (abajo del círculo) */}
                        {hasEstado && (
                          <div
                            className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center z-10"
                            style={{ backgroundColor: estadoColor }}
                          >
                            {event.estado_tipo === "check" && (
                              <i className="ri-check-line text-white text-xs"></i>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Descripción abajo */}
                      <div className="mt-5 md:mt-6 w-full px-1">
                        <p className="text-white text-xs md:text-sm leading-relaxed text-center whitespace-pre-line drop-shadow">
                          {event.descripcion}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}